<template>
  <div>
    <!-- Estado de conexión -->
    <div class="wa-connection-card mb-4">
      <div class="wa-connection-card__header">
        <div class="wa-connection-card__icon">
          <img
            src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
            width="28"
            height="28"
            alt="WhatsApp"
          />
        </div>
        <div class="wa-connection-card__info">
          <div class="font-semi-bold" style="font-size:15px;">
            {{
              config.business_name ||
                config.display_phone ||
                config.phone_number_id
            }}
          </div>
          <div style="color:#6b7280;font-size:13px;">
            {{ config.display_phone || config.phone_number }}
          </div>
        </div>
        <div class="ml-auto d-flex align-items-center" style="gap:10px;">
          <WhatsappStatusBadge :status="config.status" />
          <el-button
            size="mini"
            type="danger"
            plain
            :loading="disconnecting"
            @click="handleDisconnect"
          >
            Desconectar
          </el-button>
        </div>
      </div>
    </div>

    <!-- ── Verificación SMS ── -->
    <div
      v-if="config.status === 'needs_verification'"
      class="wa-section-card mb-4 wa-section-card--alert"
    >
      <div class="wa-section-card__title" style="color:#92400e;">
        <i class="el-icon-warning-outline mr-1"></i> Activación pendiente
      </div>

      <!-- Diagnóstico del número -->
      <div class="mb-3">
        <el-button
          size="small"
          :loading="diagnosing"
          plain
          icon="el-icon-info"
          @click="runDiagnosis"
        >
          Diagnosticar estado del número en Meta
        </el-button>
      </div>

      <!-- Resultado del diagnóstico -->
      <div
        v-if="diagnosis"
        class="mb-3"
        style="background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:10px;font-size:12px;font-family:monospace;white-space:pre-wrap;word-break:break-all;color:#374151;"
      >
        {{ JSON.stringify(diagnosis, null, 2) }}
      </div>

      <!-- Error de Meta en detalle -->
      <div
        v-if="requestCodeMetaError"
        class="mb-3"
        style="background:#fee2e2;border:1px solid #fca5a5;border-radius:8px;padding:10px;font-size:12px;"
      >
        <div style="font-weight:600;color:#991b1b;margin-bottom:4px;">
          Error Meta:
        </div>
        <div style="font-family:monospace;white-space:pre-wrap;color:#7f1d1d;">
          {{ JSON.stringify(requestCodeMetaError, null, 2) }}
        </div>
      </div>

      <div
        v-if="!verifyCodeSent"
        class="d-flex align-items-center"
        style="gap:8px;flex-wrap:wrap;"
      >
        <el-button
          :loading="requestingCode"
          style="background:#f59e0b;color:#fff;border:none;border-radius:8px;font-weight:600;"
          @click="handleRequestCode('SMS')"
        >
          <i class="el-icon-message"></i> Enviar SMS
        </el-button>
        <el-button
          :loading="requestingCodeVoice"
          plain
          style="border-radius:8px;"
          @click="handleRequestCode('VOICE')"
        >
          <i class="el-icon-phone"></i> Llamada de voz
        </el-button>
      </div>

      <div v-else>
        <div style="font-size:13px;color:#374151;margin-bottom:8px;">
          Ingresa el código de 6 dígitos:
        </div>
        <div class="d-flex align-items-center" style="gap:8px;flex-wrap:wrap;">
          <el-input
            v-model="verifyCode"
            placeholder="123456"
            maxlength="6"
            style="width:160px;"
            size="small"
            @keyup.enter.native="handleVerifyCode"
          />
          <el-button
            :loading="verifying"
            size="small"
            style="background:#1d9e75;color:#fff;border:none;border-radius:8px;font-weight:600;"
            @click="handleVerifyCode"
          >
            <i class="el-icon-circle-check"></i> Activar
          </el-button>
          <el-button
            type="text"
            size="mini"
            style="color:#6b7280;"
            @click="verifyCodeSent = false"
          >
            Reenviar
          </el-button>
        </div>
      </div>
    </div>

    <!-- Coexistencia -->
    <div v-if="config.platform_type" class="wa-section-card mb-4">
      <div class="wa-section-card__title">Modo de conexión</div>
      <div class="d-flex align-items-center mt-2" style="gap:10px;">
        <div
          v-if="config.platform_type === 'CLOUD_AND_ON_PREMISE'"
          class="wa-coex-badge wa-coex-badge--active"
        >
          <i class="el-icon-link mr-1"></i> Coexistencia activa
        </div>
        <div v-else class="wa-coex-badge wa-coex-badge--cloud">
          <i class="el-icon-cloudy mr-1"></i> Solo Cloud API
        </div>
        <div style="font-size:12px;color:#6b7280;">
          <span v-if="config.platform_type === 'CLOUD_AND_ON_PREMISE'">
            Tu número funciona con la app de WhatsApp Business
            <strong>y</strong> con Mydessk al mismo tiempo.
          </span>
          <span v-else>
            Tu número usa únicamente la Cloud API de Meta (Mydessk).
            <a
              href="https://business.facebook.com/wa/manage/phone-numbers/"
              target="_blank"
              rel="noopener"
              style="color:#3ecfb4;text-decoration:none;"
            >
              Activar coexistencia en Meta
              <i class="el-icon-top-right" style="font-size:10px;"></i>
            </a>
          </span>
        </div>
      </div>
    </div>

    <!-- Auto-respuesta -->
    <div class="wa-section-card mb-4">
      <div class="wa-section-card__title">Auto-respuesta</div>
      <div class="wa-section-card__desc mb-3">
        Cuando un cliente te escriba, se enviará esta respuesta automáticamente.
      </div>

      <div class="d-flex align-items-center mb-3">
        <span style="font-size:13px;color:#374151;margin-right:10px;"
          >Auto-respuesta</span
        >
        <el-switch
          v-model="autoReply.enabled"
          active-color="#3ecfb4"
          @change="saveAutoReply"
        />
        <span style="font-size:12px;color:#6b7280;margin-left:10px;">
          {{ autoReply.enabled ? "Activa" : "Inactiva" }}
        </span>
      </div>

      <el-input
        v-if="autoReply.enabled"
        v-model="autoReply.message"
        type="textarea"
        :rows="3"
        placeholder="Mensaje de auto-respuesta..."
        class="mb-2"
      />

      <el-button
        v-if="autoReply.enabled"
        size="small"
        :loading="savingReply"
        @click="saveAutoReply"
        style="background:#2d2b55;color:#fff;border:none;border-radius:8px;"
      >
        Guardar auto-respuesta
      </el-button>
    </div>

    <!-- IDs faltantes: WABA ID y/o Phone Number ID -->
    <div
      v-if="!config.waba_id || !config.phone_number_id"
      class="wa-section-card mb-4 wa-section-card--warning"
    >
      <div class="wa-section-card__title" style="color:#92400e;">
        <i class="el-icon-warning-outline mr-1"></i> IDs de cuenta no detectados
      </div>
      <div class="wa-section-card__desc mb-3">
        Encuéntralos en
        <strong>Meta for Developers → Tu App → WhatsApp → Configuración</strong>
        en la sección "Enviar y recibir mensajes".
      </div>

      <div v-if="!config.waba_id" class="mb-2">
        <div style="font-size:12px;color:#6b7280;margin-bottom:4px;">
          Identificador de cuenta de WhatsApp Business (WABA ID)
        </div>
        <div class="d-flex" style="gap:8px;">
          <el-input
            v-model="wabaIdInput"
            placeholder="Ej: 154355887768370"
            size="small"
            style="flex:1;"
          />
        </div>
      </div>

      <div v-if="!config.phone_number_id" class="mb-3">
        <div style="font-size:12px;color:#6b7280;margin-bottom:4px;">
          Identificador de número de teléfono (Phone Number ID)
        </div>
        <div class="d-flex" style="gap:8px;">
          <el-input
            v-model="phoneNumberIdInput"
            placeholder="Ej: 179356828587180"
            size="small"
            style="flex:1;"
          />
        </div>
      </div>

      <el-button
        size="small"
        :loading="savingWaba"
        style="background:#2d2b55;color:#fff;border:none;border-radius:8px;"
        @click="saveWabaId"
      >
        Guardar IDs
      </el-button>
    </div>

    <!-- Webhook info -->
    <div class="wa-section-card">
      <div class="wa-section-card__title">Webhook</div>
      <div class="d-flex align-items-center" style="gap:8px;">
        <span style="font-size:12px;color:#6b7280;">Estado:</span>
        <WhatsappStatusBadge
          :status="config.webhook_verified ? 'active' : 'disconnected'"
        />
      </div>
      <div
        v-if="config.verify_token"
        class="mt-2"
        style="font-size:12px;color:#6b7280;"
      >
        Verify Token: <code>{{ config.verify_token }}</code>
      </div>
    </div>
  </div>
</template>

<script>
import configService from "../../../services/configService";
import WhatsappStatusBadge from "./WhatsappStatusBadge";

export default {
  name: "WhatsappConfigPanel",
  components: { WhatsappStatusBadge },
  props: {
    config: { type: Object, required: true }
  },
  data() {
    return {
      autoReply: {
        enabled: this.config.auto_reply_enabled,
        message: this.config.auto_reply_message || ""
      },
      savingReply: false,
      disconnecting: false,
      wabaIdInput: "",
      phoneNumberIdInput: "",
      savingWaba: false,
      // Verificación SMS / diagnóstico
      requestingCode: false,
      requestingCodeVoice: false,
      requestCodeMetaError: null,
      verifyCodeSent: false,
      verifyCode: "",
      verifying: false,
      diagnosing: false,
      diagnosis: null
    };
  },
  watch: {
    config(val) {
      this.autoReply.enabled = val.auto_reply_enabled;
      this.autoReply.message = val.auto_reply_message || "";
    }
  },
  methods: {
    async saveAutoReply() {
      this.savingReply = true;
      try {
        const res = await configService.updateWhatsAppAutoReply({
          auto_reply_enabled: this.autoReply.enabled,
          auto_reply_message: this.autoReply.message
        });
        if (res.data.success) {
          this.$notifications.success({ message: "Auto-respuesta guardada." });
        }
      } finally {
        this.savingReply = false;
      }
    },

    async saveWabaId() {
      const wabaId = this.wabaIdInput.trim();
      const phoneNumberId = this.phoneNumberIdInput.trim();

      if (!this.config.waba_id && !wabaId) {
        this.$message.warning("Ingresa el WABA ID.");
        return;
      }

      this.savingWaba = true;
      try {
        const payload = {};
        if (wabaId) payload.waba_id = wabaId;
        if (phoneNumberId) payload.phone_number_id = phoneNumberId;

        const res = await configService.updateWhatsAppWabaId(payload);
        if (res.data && res.data.success) {
          this.$notifications.success({
            message: "WABA ID guardado correctamente."
          });
          this.$emit("reload");
        } else {
          this.$notifications.error({
            message: res.data.message || "Error al guardar."
          });
        }
      } finally {
        this.savingWaba = false;
      }
    },

    async runDiagnosis() {
      this.diagnosing = true;
      this.diagnosis = null;
      try {
        const res = await configService.getWhatsAppPhoneStatus();
        this.diagnosis = res.data?.data ||
          res.data || { error: "Sin respuesta" };
      } catch (e) {
        this.diagnosis = { error: e?.message || "Error al consultar Meta" };
      } finally {
        this.diagnosing = false;
      }
    },

    async handleRequestCode(method = "SMS") {
      if (method === "VOICE") {
        this.requestingCodeVoice = true;
      } else {
        this.requestingCode = true;
      }
      this.requestCodeMetaError = null;
      try {
        const res = await configService.requestWhatsAppCode(method);
        if (res.data && res.data.success) {
          if (res.data.auto_activated) {
            // El número fue verificado/registrado automáticamente — recargar sin OTP
            this.$notifications.success({
              message: res.data.message || "¡Número activado automáticamente!"
            });
            this.$emit("reload");
          } else {
            this.verifyCodeSent = true;
            this.$notifications.success({
              message:
                method === "VOICE"
                  ? "Recibirás una llamada con el código."
                  : "Código enviado. Revisa el SMS en tu número de WhatsApp Business."
            });
          }
        } else {
          // Mostrar el error completo de Meta para diagnóstico
          this.requestCodeMetaError = res.data?.meta_error || {
            message: res.data?.message
          };
          this.$notifications.error({
            message: res.data?.message || "Error al enviar el código."
          });
        }
      } finally {
        this.requestingCode = false;
        this.requestingCodeVoice = false;
      }
    },

    async handleVerifyCode() {
      if (!this.verifyCode || this.verifyCode.length !== 6) {
        this.$message.warning("El código debe tener exactamente 6 dígitos.");
        return;
      }
      this.verifying = true;
      try {
        const res = await configService.verifyWhatsAppCode({
          code: this.verifyCode
        });
        if (res.data && res.data.success) {
          this.$notifications.success({
            message: "¡Número activado! Ya puedes enviar mensajes."
          });
          this.$emit("reload");
        } else {
          this.$notifications.error({
            message:
              res.data?.message ||
              "Código incorrecto o expirado. Solicita uno nuevo."
          });
        }
      } finally {
        this.verifying = false;
      }
    },

    async handleDisconnect() {
      try {
        await this.$confirm(
          "¿Estás seguro de que deseas desconectar WhatsApp? Podrás volver a conectarlo cuando quieras.",
          "Desconectar WhatsApp",
          {
            confirmButtonText: "Sí, desconectar",
            cancelButtonText: "Cancelar",
            type: "warning"
          }
        );
      } catch {
        return;
      }

      this.disconnecting = true;
      try {
        const res = await configService.disconnectWhatsApp();
        if (res.data.success) {
          this.$notifications.success({ message: "WhatsApp desconectado." });
          this.$emit("disconnected");
        }
      } finally {
        this.disconnecting = false;
      }
    }
  }
};
</script>

<style scoped>
.wa-connection-card {
  border: 0.5px solid #e8e7e3;
  border-radius: 10px;
  padding: 16px;
  background: #fff;
}
.wa-connection-card__header {
  display: flex;
  align-items: center;
  gap: 12px;
}
.wa-connection-card__icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #f0fdf4;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.wa-section-card {
  border: 0.5px solid #e8e7e3;
  border-radius: 10px;
  padding: 16px;
  background: #fff;
}
.wa-section-card__title {
  font-weight: 600;
  font-size: 14px;
  color: #2d2b55;
  margin-bottom: 4px;
}
.wa-section-card__desc {
  font-size: 13px;
  color: #6b7280;
}
code {
  background: #f3f4f6;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
}
.wa-coex-badge {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;
  white-space: nowrap;
  flex-shrink: 0;
}
.wa-coex-badge--active {
  background: #f0fdf4;
  color: #166534;
  border: 1px solid #bbf7d0;
}
.wa-coex-badge--cloud {
  background: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
}
.wa-section-card--warning {
  border-color: #f59e0b;
  background: #fffbeb;
}
.wa-section-card--alert {
  border-color: #ef4444;
  background: #fef2f2;
}
</style>
